import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useRef, useMemo, useEffect, forwardRef, useState, useCallback, useImperativeHandle } from 'react';
import { Button } from '@andes/button';
import { constants, transformActionTarget, forEach, arrayIncludes, global, getCoachTooltipsProps, isMshops, trackEvent, getEventClickShippingCalculateTrack, stringIncludes, targetHostnameValidator } from '@vpp-frontend-components/common';
import { useVppContext } from '@vpp-frontend-components/vpp-context';
import _jsx from '@babel/runtime/helpers/jsx';
import classnames from 'classnames';
import { Tooltip } from '@andes/tooltip';
import { Icon } from '@vpp-frontend-components/icon';
import SeoAnchor from '@seo-frontend-components/seo-anchor';
import { ProgressIndicatorCircular } from '@andes/progress-indicator-circular';
import { Modal } from '@andes/modal';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
const namespace$1 = 'ui-pdp-action-modal';
const EVENT_LISTENER_NAME = 'message';

// Generic
const MODAL_REDIRECT_MESSAGE = 'vpp:modal:redirect';
const MODAL_CLOSABLE_ON_MESSAGE = 'vpp:modal:closable-on';
const MODAL_CLOSABLE_OFF_MESSAGE = 'vpp:modal:closable-off';
const CLOSE_MESSAGE = 'close=';
const SUBMIT$1 = 'submit';
const REDIRECT_TO_LOGIN = 'nav:cp:redirecttologin';

// For events switch:
const GENERICS_AND_OTHERS_MESSAGES = [CLOSE_MESSAGE, REDIRECT_TO_LOGIN, MODAL_REDIRECT_MESSAGE, MODAL_CLOSABLE_ON_MESSAGE, MODAL_CLOSABLE_OFF_MESSAGE];
const {
  modals: modals$1
} = constants;
const MODAL_TYPE = {
  SMALL: 'small',
  FULL: 'full'
};
const getUrlWithParams = (url, params) => {
  const newTabAction = {
    target: url
  };
  transformActionTarget(newTabAction, params);
  return newTabAction.target;
};
const ACTION_MODAL_DEFAULT_PROPS = {
  isFetching: false,
  autoHeight: true,
  actionTooltip: null,
  className: null,
  closeTrack: null,
  componentAsLabel: null,
  componentId: null,
  icon: null,
  iconClassName: null,
  label: null,
  linkClassName: null,
  modalClassName: null,
  modalTitle: ' ',
  modalUrl: null,
  updateShippingSummary: null,
  showCloseButton: true,
  onOpenModal: null,
  onCloseModal: null,
  submitAction: null,
  submitText: null,
  cancelText: null,
  url: '',
  track: null,
  controlModalHeader: false,
  messageId: null,
  buttonProps: null,
  buttonDisabled: false,
  showChevron: false,
  closeModalLabel: ' ',
  // Evitamos que tire warning cuando el modal no tiene boton de cierre ej:insurtech
  maxHeight: null,
  maxWidth: null,
  isTradeIn: null,
  showTooltipHandleMount: true,
  shouldRefresh: false,
  runCatchErrorBoundary: () => {},
  shouldUseSeoAnchor: false,
  wrapSimplifySeoAnchor: false
};
const _excluded$2 = ["text", "dataTestId"],
  _excluded2 = ["text", "dataTestId"];
const renderActions = actions => {
  if (!(actions && Object.keys(actions).length > 0)) {
    return null;
  }
  const _ref = (actions === null || actions === void 0 ? void 0 : actions.primary) || {},
    {
      text: primaryText,
      dataTestId: primaryDataTestId
    } = _ref,
    primaryAction = _objectWithoutProperties(_ref, _excluded$2);
  const _ref2 = (actions === null || actions === void 0 ? void 0 : actions.secondary) || {},
    {
      text: secondaryText,
      dataTestId: secondaryDataTestId
    } = _ref2,
    secondaryAction = _objectWithoutProperties(_ref2, _excluded2);
  return {
    fixed: false,
    primary: actions.primary && /*#__PURE__*/React.createElement(Button, _extends({
      "data-testid": primaryDataTestId,
      hierarchy: "loud"
    }, primaryAction), primaryText),
    secondary: actions.secondary && /*#__PURE__*/React.createElement(Button, _extends({
      "data-testid": secondaryDataTestId,
      hierarchy: "transparent"
    }, secondaryAction), secondaryText)
  };
};
const withScrollBarsHidden = show => {
  // Se calculan en base al react-remove-scroll-bar y cumplen la función de suplantar el espacio
  // dejado por la scrollbar en los casos que se utilice mouse
  // checks para omitir el test del dom
  const main = document.querySelectorAll('body > header, body > main') || [];
  forEach(main, element => {
    element.classList[show ? 'add' : 'remove']('with-scroll-bars-hidden');
  });
};
const useActionModalRef = customRef => {
  const defaultRef = useRef(null);
  return customRef === undefined ? defaultRef : customRef;
};
const _excluded$1 = ["customRef"];
const withBusinessLogic = function (Component) {
  let getConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => null;
  const ComponentWithBusinessLogic = _ref => {
    let {
        customRef
      } = _ref,
      props = _objectWithoutProperties(_ref, _excluded$1);
    const ref = useActionModalRef(customRef);
    const context = useVppContext();
    const config = useMemo(() => getConfig(props, ref, context), [props, ref, context]);
    return /*#__PURE__*/React.createElement(Component, _extends({
      ref: ref
    }, props, config));
  };
  ComponentWithBusinessLogic.displayName = 'WithBusinessLogic';
  return ComponentWithBusinessLogic;
};
const _excluded = ["onClickButton", "text"];

/**
 * ActionModalButton component
 * @typedef {import('../../index').ActionModalButton}
 */
const ActionModalButton = _ref => {
  let {
      onClickButton,
      text
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Button, _extends({
    className: `${namespace$1}-button`,
    "data-testid": "action-modal-button",
    onClick: onClickButton
  }, rest), text);
};

/**
 * ModalContent component
 * @typedef {import('../../index').ModalContent}
 */
const namespace = 'ui-pdp-action-modal-content';
const headerSelector = '.andes-modal__header';
const ModalContent = _ref => {
  let {
    children = null,
    className = '',
    extra = 10,
    modalClassName,
    viewportHeightRatio = 0.7
  } = _ref;
  const ref = useRef();
  useEffect(() => {
    var _ref$current, _ref$current2;
    const contentHeight = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientHeight;
    const offset = (_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.offsetTop;
    const container = document.querySelector(`.${modalClassName}`);
    let headerDiff = 0;
    if (container) {
      const viewportHeight = window.innerHeight;
      {
        const header = container.querySelector(headerSelector);
        headerDiff = header ? header.clientHeight : 0;
      }
      const availableHeight = viewportHeight * viewportHeightRatio - headerDiff;
      if (contentHeight < availableHeight) {
        container.style.height = `${contentHeight + offset + headerDiff + extra}px`;
      }
    }
  }, [extra, modalClassName, ref, viewportHeightRatio]);
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(namespace, className, `${namespace}__container`),
    ref: ref
  }, children);
};

/**
 * ActionModalContent component
 * @typedef {import('../../index').ActionModalContent}
 */
const ActionModalContent = _ref => {
  let {
    autoHeight,
    isMobile,
    modalClassName,
    children
  } = _ref;
  return autoHeight && !isMobile ? /*#__PURE__*/_jsx(ModalContent, {
    modalClassName: modalClassName
  }, void 0, children) : children;
};
var _Icon;
const chevronCn = 'ui-pdp-icon--chevron-right';

/**
 * ActionModalTooltip component
 * @typedef {import('../../index').ActionModalTooltip}
 */
const ActionModalTooltip = _ref => {
  let {
    classnames,
    tooltipProps,
    shouldShowTooltip,
    target,
    onClickTooltip,
    icon,
    labelText,
    showChevron,
    shouldUseSeoAnchor,
    wrapSimplifySeoAnchor
  } = _ref;
  const {
    cnWrapper,
    cnIcon,
    cnLink
  } = classnames;
  const AnchorTag = shouldUseSeoAnchor ? SeoAnchor : 'a';
  const anchorProps = shouldUseSeoAnchor ? {
    wrapSimplifySeoAnchor
  } : {};
  return /*#__PURE__*/_jsx("div", {
    className: cnWrapper
  }, void 0, /*#__PURE__*/React.createElement(Tooltip, _extends({}, tooltipProps, {
    open: shouldShowTooltip
  }), /*#__PURE__*/React.createElement(AnchorTag, _extends({
    className: cnLink,
    href: target,
    onClick: onClickTooltip,
    "data-testid": "action-modal-link"
  }, anchorProps), icon && /*#__PURE__*/_jsx("div", {
    className: cnIcon
  }, void 0, icon), labelText, showChevron && (_Icon || (_Icon = /*#__PURE__*/_jsx(Icon, {
    id: "chevron",
    className: chevronCn
  }))))));
};
var _ProgressIndicatorCir;

/**
 * ActionModalUI component
 * @typedef {import('../../index').ActionModalUI}
 */
const getIsMobile = deviceType => deviceType === 'mobile';
const validateKeys = e => (e === null || e === void 0 ? void 0 : e.ctrlKey) || (e === null || e === void 0 ? void 0 : e.metaKey);
const ActionModalRaw = function () {
  let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ACTION_MODAL_DEFAULT_PROPS;
  let ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const {
    type = null,
    isVisible = false,
    srLabel = null,
    trigger = null,
    autoHeight = true,
    buttonDisabled = false,
    buttonConfig = null,
    children = null,
    closeModalLabel = ' ',
    componentId = null,
    controlModalHeader = false,
    customEventMessageHandler = null,
    maxHeight = null,
    maxWidth = null,
    modalActions = null,
    modalClassName = null,
    linkClassName = null,
    modalTitle = ' ',
    modalUrl = null,
    onCloseModal = null,
    onOpenModal = null,
    redirectToLoginHandler: redirectToLogin = null,
    redirectToTargetHandler: redirectToTarget = null,
    showCloseButton = true,
    showTooltipHandleMount = true,
    tooltipConfig = null,
    url = '',
    shouldRefresh: initialValueRefresh = false,
    runCatchErrorBoundary = () => {},
    shouldUseSeoAnchor = false,
    wrapSimplifySeoAnchor = false
  } = props;
  try {
    /* eslint-disable react-hooks/rules-of-hooks */
    const {
      tooltipProps,
      visible
    } = tooltipConfig || {};
    const [isOpenModal, setIsOpenModal] = useState(isVisible);
    const [showLoading, setShowLoading] = useState(false);
    const [shouldShowCloseBtn, setShouldShowCloseBtn] = useState(true);
    const [shouldShowTooltip, setShouldShowTooltip] = useState(visible !== null && visible !== void 0 ? visible : null);
    const [shouldBeClosable, setShouldBeClosable] = useState(true);
    const [scrolled, setScrolled] = useState(false);
    const [shouldRefresh, setShouldRefresh] = useState(initialValueRefresh);
    const [shouldShowTitle, setShouldShowTitle] = useState(true);
    const [redirected, setRedirected] = useState(false);
    const {
      staticProps
    } = useVppContext();
    const {
      deviceType,
      isWebview
    } = staticProps;
    const isMobile = getIsMobile(deviceType);
    const TriggerAnchorTag = shouldUseSeoAnchor ? SeoAnchor : 'a';
    const triggerElement = /*#__PURE__*/React.isValidElement(trigger) ? trigger : trigger === null || trigger === void 0 ? void 0 : trigger.text;
    const openModal = useCallback(event => {
      if (isOpenModal || buttonDisabled || validateKeys(event) && url) {
        return;
      }

      // Cuando se llama la función por fuera del componente (ref), esta no recibe el parametro event.
      if (event !== null && event !== void 0 && event.preventDefault) {
        event.preventDefault();
      }
      if (!isMobile) {
        withScrollBarsHidden(true);
      }
      if (onOpenModal) {
        onOpenModal(event);
      }
      setIsOpenModal(true);
      setShowLoading(true);
      setShouldShowCloseBtn(true);
    }, [buttonDisabled, isMobile, isOpenModal, onOpenModal, url]);
    const hideModal = useCallback(() => {
      setIsOpenModal(false);
      setShowLoading(false);
    }, []);
    const onHideModal = useCallback(event => {
      hideModal();
      if (!isMobile) {
        withScrollBarsHidden(false);
      }
      if (onCloseModal) {
        onCloseModal(event, {
          shouldRefresh
        });
      }
    }, [hideModal, isMobile, onCloseModal, shouldRefresh]);
    const eventMessageSuscriptor = useCallback(event => {
      const {
        data
      } = event;
      const {
        target
      } = data || {};
      const key = typeof data === 'string' ? data : data === null || data === void 0 ? void 0 : data.message;
      if (key && arrayIncludes(GENERICS_AND_OTHERS_MESSAGES, key)) {
        switch (key) {
          case CLOSE_MESSAGE:
            hideModal();
            break;
          case REDIRECT_TO_LOGIN:
            redirectToLogin(data);
            break;
          case MODAL_REDIRECT_MESSAGE:
            redirectToTarget(target);
            break;
          case MODAL_CLOSABLE_ON_MESSAGE:
            setShouldBeClosable(true);
            break;
          case MODAL_CLOSABLE_OFF_MESSAGE:
            setShouldBeClosable(false);
            break;
          default:
            break;
        }
      } else {
        customEventMessageHandler === null || customEventMessageHandler === void 0 || customEventMessageHandler(event, {
          redirected
        });
      }
    }, [customEventMessageHandler, redirectToLogin, redirectToTarget, redirected, hideModal]);
    useEffect(() => {
      if (!global.addEventListener) {
        return;
      }
      if (isOpenModal) {
        global.addEventListener(EVENT_LISTENER_NAME, eventMessageSuscriptor);
      }
      return () => {
        global.removeEventListener(EVENT_LISTENER_NAME, eventMessageSuscriptor);
      };
    }, [isOpenModal, eventMessageSuscriptor]);
    useEffect(() => {
      if (tooltipProps !== null && tooltipProps !== void 0 && tooltipProps.id && shouldShowTooltip === null) {
        if (showTooltipHandleMount) {
          setShouldShowTooltip(true);
        }
      }
    }, [tooltipProps, shouldShowTooltip, showTooltipHandleMount]);
    useImperativeHandle(ref, () => ({
      setIsOpenModal,
      setShowLoading,
      setShouldShowCloseBtn,
      setShouldShowTooltip,
      setShouldBeClosable,
      setScrolled,
      setShouldRefresh,
      setShouldShowTitle,
      openModal,
      hideModal,
      setRedirected
    }), [openModal, hideModal]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, !!buttonConfig && /*#__PURE__*/React.createElement(ActionModalButton, _extends({
      onClickButton: openModal
    }, buttonConfig)), !!tooltipConfig && Object.keys(tooltipConfig).length > 0 && /*#__PURE__*/React.createElement(ActionModalTooltip, _extends({}, tooltipConfig, {
      shouldShowTooltip: shouldShowTooltip,
      shouldUseSeoAnchor: shouldUseSeoAnchor !== null && shouldUseSeoAnchor !== void 0 ? shouldUseSeoAnchor : false,
      wrapSimplifySeoAnchor: wrapSimplifySeoAnchor !== null && wrapSimplifySeoAnchor !== void 0 ? wrapSimplifySeoAnchor : false
    })), !!triggerElement && /*#__PURE__*/_jsx(TriggerAnchorTag, {
      className: classnames(`${namespace$1}__link`, linkClassName),
      href: getUrlWithParams(url),
      onClick: openModal,
      "data-testid": "action-modal-link",
      wrapSimplifySeoAnchor: wrapSimplifySeoAnchor
    }, void 0, triggerElement), /*#__PURE__*/_jsx(Modal, {
      actions: renderActions(modalActions),
      url: modalUrl,
      closeUrl: "",
      className: classnames(`${namespace$1}__modal`, modalClassName, {
        [`${componentId}__modal`]: componentId,
        [`${namespace$1}__modal--no-title`]: !modalTitle || !shouldShowTitle,
        [`${namespace$1}__modal--no-close`]: !shouldShowCloseBtn
      }),
      open: isOpenModal,
      onClose: event => onHideModal({
        event
      }),
      onOpen: () => setShowLoading(false),
      title: modalTitle,
      type: type || (isMobile ? MODAL_TYPE.FULL : MODAL_TYPE.SMALL),
      showCloseButton: showCloseButton && shouldShowCloseBtn && !isWebview,
      closeButtonSrLabel: closeModalLabel,
      headerControlled: controlModalHeader,
      headerControlledFlag: controlModalHeader && scrolled,
      maxWidth: maxWidth,
      maxHeight: maxHeight,
      closable: shouldBeClosable,
      srLabel: srLabel
    }, void 0, /*#__PURE__*/_jsx(ActionModalContent, {
      modalClassName: modalClassName,
      autoHeight: autoHeight,
      isMobile: isMobile
    }, void 0, showLoading && (_ProgressIndicatorCir || (_ProgressIndicatorCir = /*#__PURE__*/_jsx(ProgressIndicatorCircular, {
      modifier: "fullscreen",
      size: "large"
    }))), children)));
    /* eslint-enable react-hooks/rules-of-hooks */
  } catch (error) {
    /* istanbul ignore next */
    return runCatchErrorBoundary(error);
  }
};
const ActionModalUI = /*#__PURE__*/forwardRef(ActionModalRaw);
ActionModalUI.displayName = 'ActionModalUI';

/* eslint-disable @typescript-eslint/no-unused-expressions */

const getActions = (props, ref) => {
  const {
    submitAction,
    submitText,
    isTradeIn,
    isFetching,
    cancelText
  } = props;
  const closeModalHub = () => {
    var _ref$current, _ref$current$setIsOpe, _ref$current2, _ref$current2$setShow;
    (_ref$current = ref.current) === null || _ref$current === void 0 || (_ref$current$setIsOpe = _ref$current.setIsOpenModal) === null || _ref$current$setIsOpe === void 0 || _ref$current$setIsOpe.call(_ref$current, false);
    (_ref$current2 = ref.current) === null || _ref$current2 === void 0 || (_ref$current2$setShow = _ref$current2.setShowLoading) === null || _ref$current2$setShow === void 0 || _ref$current2$setShow.call(_ref$current2, false);
  };
  const hideModal = () => {
    var _ref$current3, _ref$current3$hideMod;
    (_ref$current3 = ref.current) === null || _ref$current3 === void 0 || (_ref$current3$hideMod = _ref$current3.hideModal) === null || _ref$current3$hideMod === void 0 || _ref$current3$hideMod.call(_ref$current3);
  };
  return submitAction && submitText && {
    fixed: false,
    primary: {
      dataTestId: 'variations-button',
      className: 'ui-pdp-variations__modal__button',
      onClick: e => {
        submitAction(e);
        isTradeIn ? closeModalHub() : hideModal();
      },
      hierarchy: 'loud',
      disabled: isFetching,
      text: submitText
    },
    secondary: cancelText && {
      dataTestId: 'cancel-button',
      className: 'ui-pdp-variations__modal__button cancel-button',
      onClick: () => {
        closeModalHub();
      },
      hierarchy: 'transparent',
      disabled: isFetching,
      text: cancelText
    }
  };
};
const TRACK_PAGE_DEFAULT = 'vip';

// Directions Hub
const SHIPPING_HUB_EVENT_LOADED = 'nav:cp:loaded';
const SHIPPING_HUB_EVENT_UPDATE = 'nav:cp:update';
const SHIPPING_HUB_EVENT_CLOSE = 'nav:cp:close';
const SHIPPING_HUB_EVENT_REDIRECT = 'nav:cp:redirect';

// Shipping Calculator
const SHIPPING_CALCULATOR_REFRESH = 'vip:shipping-calculator:refreshpage';
const SHIPPING_CALCULATOR_UPDATE = 'vip:shipping-calculator:update';
const SHIPPING_CALCULATOR_CLOSE = 'vip:shipping-calculator:close';
const SHIPPING_CALCULATOR_MODAL_BTN_SHOW = 'vip:shipping-calculator-modalbtn:show';
const SHIPPING_CALCULATOR_MODAL_BTN_HIDE = 'vip:shipping-calculator-modalbtn:hide';
const SUBMIT = 'submit';

// Shipping (cp + calculator)
const SHIPPING_MESSAGES = [SHIPPING_HUB_EVENT_LOADED, SHIPPING_HUB_EVENT_UPDATE, SHIPPING_HUB_EVENT_CLOSE, SHIPPING_HUB_EVENT_REDIRECT, SHIPPING_CALCULATOR_REFRESH, SHIPPING_CALCULATOR_UPDATE, SHIPPING_CALCULATOR_CLOSE, SHIPPING_CALCULATOR_MODAL_BTN_SHOW, SHIPPING_CALCULATOR_MODAL_BTN_HIDE, SUBMIT];

// Meliplus & MDD
const MELIPLUS_SUSCRIPTION_MESSAGE = 'vpp:meliplus:suscription';
const MDD_CHANGE_MESSAGE = 'vpp:mdd:change';
const {
    mshops: {
      GUEST_ADDITIONAL_INFO,
      CHO_GUEST_STYLE
    }
  } = constants,
  mshops = _objectWithoutProperties(constants.mshops, ["GUEST_ADDITIONAL_INFO", "CHO_GUEST_STYLE"]);
const {
  modals
} = constants;
const getTooltipConfig = staticProps => (_ref, ref) => {
  let {
    componentId,
    modalUrl,
    url,
    linkClassName,
    className = '',
    iconClassName,
    componentAsLabel,
    actionTooltip,
    showChevron,
    icon,
    label
  } = _ref;
  const {
    cpCookie,
    page,
    site
  } = staticProps;
  const trackPage = page || TRACK_PAGE_DEFAULT;
  const tooltipProps = getCoachTooltipsProps(actionTooltip, classnames(namespace$1, '__shipping_summary'));
  const params = {
    modal: false
  };
  const target = getUrlWithParams(url, params);
  const onClickTooltip = e => {
    var _ref$current, _ref$current$openModa;
    const sendShippingCoachTracks = isMshops({
      platform: {
        id: site
      }
    }) && (componentId === mshops.SHIPPING_COMPONENT || (modalUrl === null || modalUrl === void 0 ? void 0 : modalUrl.includes(modals.SHIPPING)));
    if (e !== null && e !== void 0 && e.target && sendShippingCoachTracks) {
      trackEvent(getEventClickShippingCalculateTrack(trackPage, sendShippingCoachTracks, cpCookie));
    }
    (_ref$current = ref.current) === null || _ref$current === void 0 || (_ref$current$openModa = _ref$current.openModal) === null || _ref$current$openModa === void 0 || _ref$current$openModa.call(_ref$current, e);
  };
  const tooltipClassnames = {
    cnWrapper: classnames(namespace$1, isMshops({
      platform: {
        id: site
      }
    }) && linkClassName === GUEST_ADDITIONAL_INFO ? `${className} ${CHO_GUEST_STYLE}` : className),
    cnIcon: classnames(className, iconClassName, {
      [`${namespace$1}__icon`]: !componentAsLabel
    }),
    cnLink: classnames(`${namespace$1}__link`, linkClassName)
  };
  return {
    classnames: tooltipClassnames,
    tooltipProps,
    target,
    onClickTooltip,
    icon,
    labelText: (label === null || label === void 0 ? void 0 : label.text) || componentAsLabel,
    showChevron
  };
};

/* eslint-disable complexity */
function eventMessageHandler(event, props, state, ref) {
  const {
    controlModalHeader,
    meliplusUpsellingOnSuscription,
    meliplusOnDeliveryDayChange
  } = props;
  const {
    redirected
  } = state;
  const methods = ref.current || {};
  const {
    setShouldShowCloseBtn,
    setShouldRefresh,
    setShowLoading,
    setScrolled,
    hideModal,
    setIsOpenModal,
    setRedirected
  } = methods;
  const {
    data
  } = event;
  const {
    type,
    message,
    url,
    origin
  } = data;

  // meliplus & MDD
  if (message) {
    switch (message) {
      case MELIPLUS_SUSCRIPTION_MESSAGE:
        meliplusUpsellingOnSuscription === null || meliplusUpsellingOnSuscription === void 0 || meliplusUpsellingOnSuscription();
        break;
      case MDD_CHANGE_MESSAGE:
        meliplusOnDeliveryDayChange === null || meliplusOnDeliveryDayChange === void 0 || meliplusOnDeliveryDayChange();
        break;
    }
  }
  if (controlModalHeader && type && stringIncludes('vip:modal-content:scrolled', type)) {
    if (event.data.scrolled) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  }
  if (type && arrayIncludes(SHIPPING_MESSAGES, type)) {
    switch (type) {
      case SHIPPING_CALCULATOR_MODAL_BTN_SHOW:
        setShowLoading(false);
        setShouldShowCloseBtn(true);
        if (redirected) {
          hideModal();
        }
        break;
      case SHIPPING_CALCULATOR_MODAL_BTN_HIDE:
        setShowLoading(true);
        setShouldShowCloseBtn(true);
        break;
      case SHIPPING_HUB_EVENT_LOADED:
        setShowLoading(false);
        setShouldShowCloseBtn(true);
        break;
      case SHIPPING_HUB_EVENT_REDIRECT:
        setRedirected(true);
        setShouldRefresh(true);
        break;
      case SHIPPING_CALCULATOR_REFRESH:
      case SHIPPING_HUB_EVENT_UPDATE:
        setShouldRefresh(true);
        break;
      case SUBMIT:
        if (typeof window !== 'undefined' && url && origin && origin === 'loyalty' && targetHostnameValidator({
          target: url,
          currentLocation: window.location
        })) {
          window.location = url;
        }
        break;
      case SHIPPING_HUB_EVENT_CLOSE:
        setIsOpenModal(false);
        setShowLoading(false);
        break;
      default:
        hideModal({
          event
        });
        break;
    }
  }
}
const getButtonConfig = (_ref, ref) => {
  let {
    buttonProps,
    label
  } = _ref;
  return label && buttonProps && _objectSpread(_objectSpread(_objectSpread({}, buttonProps), {}, {
    onClickButton: e => {
      var _ref$current, _ref$current$openModa;
      return (_ref$current = ref.current) === null || _ref$current === void 0 || (_ref$current$openModa = _ref$current.openModal) === null || _ref$current$openModa === void 0 ? void 0 : _ref$current$openModa.call(_ref$current, e);
    }
  }, buttonProps !== null && buttonProps !== void 0 && buttonProps.onClick ? {
    onClickButton: buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.onClick
  } : {}), {}, {
    text: label === null || label === void 0 ? void 0 : label.text
  });
};

/* 

Por default se exporta esta plantilla para usar el componente con toda la lógica de negocio que ya venia arrastrando.
Lo ideal sería que armes tu propia configuración y se la inyectes en el componentes ActionModalUI.

*/

const reloadPage = () => setTimeout(() => window.location.reload(true), 0);
const buildOnCloseModal = (props, HistoryService, refreshPage) => (event, _ref) => {
  let {
    shouldRefresh
  } = _ref;
  const {
    onCloseModal,
    modalUrl,
    closeTrack
  } = props;
  if (event !== null && event !== void 0 && event.target) {
    trackEvent(closeTrack);
  }
  onCloseModal === null || onCloseModal === void 0 || onCloseModal(event);

  // Usado para el componente de ShippingCalculator (fury_navigation-frontend)
  if (shouldRefresh) {
    refreshPage();
  }
  /* -- */

  if (modalUrl && (event === null || event === void 0 ? void 0 : event.data) !== CLOSE_MESSAGE) {
    HistoryService.back();
  }
};
const ActionModal = withBusinessLogic(ActionModalUI, (props, ref, context) => {
  const {
    staticProps,
    dependencies
  } = context;
  const {
    site
  } = staticProps;
  const {
    APIService,
    HistoryService
  } = dependencies;
  const modalActions = getActions(props, ref);
  const buttonConfig = getButtonConfig(props, ref);
  const shouldHaveTooltipConfig = (props.label || props.componentAsLabel) && !props.trigger && !buttonConfig;
  const tooltipConfig = shouldHaveTooltipConfig && getTooltipConfig(staticProps)(props, ref);
  const showTooltipHandleMount = !isMshops({
    platform: {
      id: site
    }
  });
  const customOnOpenModal = event => {
    const {
      onOpenModal,
      componentId,
      track
    } = props;
    trackEvent(track);
    onOpenModal === null || onOpenModal === void 0 || onOpenModal(event);

    // Usado para el componente de ShippingSummary
    if (componentId === 'shipping_summary') {
      var _ref$current, _ref$current$setShoul;
      (_ref$current = ref.current) === null || _ref$current === void 0 || (_ref$current$setShoul = _ref$current.setShouldShowTooltip) === null || _ref$current$setShoul === void 0 || _ref$current$setShoul.call(_ref$current, false);
    }
    /* -- */
  };
  const customOnCloseModal = buildOnCloseModal(props, HistoryService, reloadPage);
  const redirectToLoginHandler = data => {
    try {
      APIService.redirectToLogin(data.itemId, 'reviews', 'reviews');
    } catch (error) {
      throw new Error(error);
    }
  };
  const redirectToTargetHandler = target => {
    if (target && targetHostnameValidator({
      target,
      currentLocation: global.location
    })) {
      global.location = target;
    }
  };
  const customEventMessageHandler = (event, state) => {
    eventMessageHandler(event, props, state, ref);
  };
  return {
    modalActions,
    tooltipConfig,
    buttonConfig,
    showTooltipHandleMount,
    customEventMessageHandler,
    onOpenModal: customOnOpenModal,
    onCloseModal: customOnCloseModal,
    redirectToLoginHandler,
    redirectToTargetHandler
  };
});
export { ACTION_MODAL_DEFAULT_PROPS, ActionModal, ActionModalButton, ActionModalContent, ActionModalTooltip, ActionModalUI, CLOSE_MESSAGE, EVENT_LISTENER_NAME, GENERICS_AND_OTHERS_MESSAGES, MODAL_CLOSABLE_OFF_MESSAGE, MODAL_CLOSABLE_ON_MESSAGE, MODAL_REDIRECT_MESSAGE, MODAL_TYPE, ModalContent, REDIRECT_TO_LOGIN, SUBMIT$1 as SUBMIT, buildOnCloseModal, getUrlWithParams, modals$1 as modals, namespace$1 as namespace, renderActions, useActionModalRef, withBusinessLogic, withScrollBarsHidden };
